/* eslint-disable ember/no-get */
/* eslint-disable ember/no-computed-properties-in-native-classes */
import type NativeArray from '@ember/array/-private/native-array';
import { get, computed } from '@ember/object';
import { filterBy } from '@ember/object/computed';
import Model, { attr } from '@ember-data/model';
import adapter from 'garaje/utils/decorators/adapter';
import { idScanningHoldTileMessage } from 'garaje/utils/id-scanning-hold-copy';
import ReportFactory from 'garaje/utils/report-factory';

export interface VisualComplianceRpsResult {
  id?: string;
  frc?: string;
  city?: string;
  list?: string;
  name?: string;
  type?: string;
  notes?: string;
  state?: string;
  country?: string;
  enddate?: string;
  frserve?: string;
  street1?: string;
  street2?: string;
  category?: string;
  ctrycode?: string;
  alerttype?: string;
  startdate?: string;
}

export type ReportSource =
  | 'blacklist'
  | 'capacity-screening'
  | 'conditional-rule'
  | 'employee-screening'
  | 'id-scanning'
  | 'identity-verification'
  | 'pre-registration-required'
  | 'property-invite-approval'
  | 'requires-approval'
  | 'temperature-screening'
  | 'visitor-documents'
  | 'visual-compliance'
  | 'visit-workflows';

export interface Report {
  blacklistFilterId?: string;
  blacklistFilterSource?: string;
  blacklistFilterSourceId?: string;
  result: string;
  reviewable: boolean;
  source: ReportSource;
  status: string;
  reason?: string;
  messages: {
    failure: {
      text: string;
    };
  };
  veriffSessionId?: string | null;

  // Visual Compliance
  'alert-level'?: number | null;
  'call-status'?: string;
  count?: number;
  'risk-country'?: boolean;
  'rps-results'?: VisualComplianceRpsResult[];
}

@adapter('v1')
class ApprovalStatusModel extends Model {
  @attr('string') declare status: string;
  @attr('string') declare reviewerName: string;
  @attr('string') declare reviewedAt: string;
  @attr('boolean') declare employeeTriggeredRescreen: boolean;

  // @ts-ignore - using custom options. Refactor?
  @attr('immutable-array', {
    serializeItem: (item: ApprovalStatusModel) => item.serialize(),
    deserializeItem: (item: ApprovalStatusModel) => ReportFactory.createReport(item),
  })
  declare report: NativeArray<Report>;

  @filterBy('report', 'result', 'fail') failedReport!: Report[];
  @filterBy('report', 'source', 'blacklist') blocklistReport!: Report[];
  @filterBy('report', 'source', 'pre-registration-required') preRegistrationRequiredReport!: Report[];
  @filterBy('report', 'source', 'id-scanning') idScanningReport!: Report[];
  @filterBy('report', 'source', 'visual-compliance') visualComplianceReport!: Report[];
  @filterBy('report', 'source', 'property-invite-approval') propertyInviteApproval!: Report[];
  @filterBy('report', 'source', 'requires-approval') inviteApproval!: Report[];

  @computed('failedReport.@each.source') get didFailBlocklistCheck(): boolean {
    const reports = this.failedReport;
    return (reports || []).some((report) => report.source === 'blacklist');
  }

  @computed('failedReport.@each.source') get didFailPreRegistrationRequiredCheck(): boolean {
    const reports = this.failedReport;
    return (reports || []).some((report) => report.source === 'pre-registration-required');
  }

  @computed('failedReport.@each.source') get didFailIdScanningCheck(): boolean {
    const reports = this.failedReport;
    return (reports || []).some((report) => report.source === 'id-scanning');
  }

  @computed('failedReport.@each.source') get didFailVisualComplianceCheck(): boolean {
    const reports = this.failedReport;
    return (reports || []).some((report) => report.source === 'visual-compliance');
  }

  @computed('failedReport.@each.{source,call-status}') get didNotContactVisualCompliance(): boolean {
    const reports = this.failedReport;
    return (reports || []).some(
      (report) => report.source === 'visual-compliance' && report['call-status'] === 'unreachable',
    );
  }

  @computed('failedReport.@each.source') get didFailTempScreenCheck(): boolean {
    const reports = this.failedReport;
    return (reports || []).some((report) => report.source === 'temperature-screening');
  }

  @computed('failedReport.@each.source') get didFailIdVerificationCheck(): boolean {
    const reports = this.failedReport;
    return (reports || []).some((report) => report.source === 'identity-verification');
  }

  @computed('failedReport.@each.source') get tempCheckFailureMessage(): string {
    const report = (this.failedReport || []).find((report) => report.source === 'temperature-screening');
    return report ? <string>get(report, 'messages.failure.text') : '';
  }

  @computed('failedReport.@each.source') get idVerificationFailureMessage(): string {
    const report = (this.failedReport || []).find((report) => report.source === 'identity-verification');
    return report ? <string>get(report, 'messages.failure.text') : '';
  }

  get idScanningFailureMessage(): string {
    const report = (this.failedReport || []).find((report) => report.source === 'id-scanning');
    return report ? idScanningHoldTileMessage(report) : '';
  }

  @computed('didFailIdScanningCheck', 'report.@each.{source,result}') get hasPendingIdScan(): boolean {
    const reports = this.report || [];
    const hasFailedState = this.didFailIdScanningCheck;
    const hasPassedState = reports.any((r) => r.source === 'id-scanning' && r.result === 'pass');
    const hasPendingState = reports.any((r) => r.source === 'id-scanning' && r.result === 'pending');
    return !hasFailedState && !hasPassedState && hasPendingState;
  }

  @computed('failedReport.@each.source') get isAtCapacity(): boolean {
    const reports = this.failedReport;
    return (reports || []).some((report) => report.source === 'capacity-screening');
  }

  @computed('failedReport.@each.source') get visitorDocumentsCheck(): Report | undefined {
    return (this.failedReport || []).find((report) => report.source === 'visitor-documents');
  }
}

export default ApprovalStatusModel;
